import React from "react";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import Seo, { breadcrumbSchema } from "@/components/Seo";
import PageHero from "@/components/PageHero";
import MapEmbed from "@/components/MapEmbed";
import { Reveal } from "@/components/Reveal";
import { BookButton, SectionHeading } from "@/components/ui-bits";
import { HOTEL, IMAGES } from "@/lib/site";

const CARDS = [
  { icon: Phone, label: "Call Us", value: HOTEL.phone, href: HOTEL.phoneHref, sub: `Toll-free ${HOTEL.tollFree}`, testid: "contact-phone" },
  { icon: Mail, label: "Email Us", value: HOTEL.email, href: HOTEL.emailHref, sub: "We reply within one business day", testid: "contact-email" },
  { icon: MapPin, label: "Visit Us", value: HOTEL.fullAddress, href: HOTEL.mapDirections, sub: "Just off US Highway 69", testid: "contact-address" },
  { icon: Clock, label: "Front Desk", value: "Open 24 Hours", sub: "Check-in 3PM · Check-out 11AM", testid: "contact-hours" },
];

export default function Contact() {
  return (
    <>
      <Seo
        title="Contact Regency Inn & Suites | Pittsburg, KS Hotel"
        description="Contact Regency Inn & Suites in Pittsburg, Kansas. Call 620-232-2800, email info@regencyinnpittsburg.com, or visit us at 4023 Parkview Drive. 24-hour front desk."
        path="/contact"
        image={IMAGES.heroFacade}
        schema={[breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }])]}
      />
      <PageHero
        eyebrow="Get in Touch"
        title="We'd love to hear from you."
        subtitle="Call, email or stop by — our team is here around the clock to help with reservations, group bookings and extended-stay rates."
        image={IMAGES.heroFacade}
        crumbs={[{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }]}
      />

      <section className="bg-bone py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
            {CARDS.map((c) => {
              const Icon = c.icon;
              const inner = (
                <div className="border border-stone/15 rounded-md p-7 h-full hover:border-amber transition-colors">
                  <Icon className="text-amber" size={26} strokeWidth={1.4} />
                  <p className="text-xs uppercase tracking-widest text-amber-deep mt-4">{c.label}</p>
                  <p className="font-serif text-xl text-forest mt-1">{c.value}</p>
                  <p className="text-sm text-stone mt-1">{c.sub}</p>
                </div>
              );
              return c.href ? (
                <a key={c.label} href={c.href} target={c.href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" data-testid={c.testid}>
                  {inner}
                </a>
              ) : (
                <div key={c.label} data-testid={c.testid}>{inner}</div>
              );
            })}
          </div>

          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <Reveal>
              <SectionHeading eyebrow="Book Direct" title="Reserve your room in seconds." />
              <p className="mt-6 text-stone leading-relaxed max-w-md">
                The fastest way to lock in our best available rate is to book directly through our secure reservation
                system — no OTA fees, just a warm welcome waiting for you in Pittsburg.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <BookButton label="Book Direct & Save" testid="contact-book" />
                <a href={HOTEL.phoneHref} data-testid="contact-call-btn" className="inline-flex items-center rounded-full border border-forest text-forest px-8 py-4 text-sm font-medium hover:bg-forest hover:text-bone transition-colors">
                  Call {HOTEL.phone}
                </a>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <MapEmbed height="440px" />
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
